import {
  Component,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { v4 as uuidv4 } from 'uuid';
import { ChecklistHttpService } from '../../../../services/checklist-http.service';
import { OfflineStorageService } from '../../../../services/offline/offline-storage.service';
import { AppToastService } from '../../../../services/app-toast.service';
import { FileRef, FileMetadata } from './file-upload.types';
import { PopoverComponent } from '../../popover/popover.component';
import { AppButton, ButtonType } from '../../button/button.component';
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [CdkOverlayOrigin, PopoverComponent, AppButton, SvgIconComponent],
  templateUrl: './file-upload.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent {
  readonly checklistId = input('');
  readonly itemId = input('');
  readonly customInputId = input('');
  readonly maxFiles = input(5);
  readonly files = input<FileRef[]>([]);
  readonly accept = input('image/png,image/jpeg,image/webp,image/heic');
  readonly readOnly = input(false);
  readonly helperText = input('');
  readonly buttonLabel = input('Add picture');
  readonly compactMode = input(false);
  // Awaited before the upload is queued (promotes a Preview checklist), and a rejection skips the upload and evicts the cached blob.
  readonly beforePersist = input<() => Promise<void>>();

  readonly ButtonType = ButtonType;

  readonly fileUploaded = output<FileMetadata>();
  readonly fileRemoved = output<string>();
  readonly uploadError = output<{
    file: File;
    error: unknown;
  }>();

  readonly cameraInput =
    viewChild.required<ElementRef<HTMLInputElement>>('cameraInput');
  readonly fileInput =
    viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  readonly overlayOrigin = viewChild.required(CdkOverlayOrigin);

  popoverOpen = false;
  uploading = false;

  private readonly http = inject(ChecklistHttpService);
  private readonly offlineStorage = inject(OfflineStorageService);
  private readonly appToastService = inject(AppToastService);
  private readonly cdr = inject(ChangeDetectorRef);

  togglePopover(event?: Event | unknown): void {
    if (event instanceof Event) {
      event.stopPropagation();
    }
    this.popoverOpen = !this.popoverOpen;
  }

  closePopover(): void {
    this.popoverOpen = false;
  }

  onTakePhoto(): void {
    this.popoverOpen = false;
    this.cameraInput().nativeElement.value = '';
    this.cameraInput().nativeElement.click();
  }

  onUploadFromDevice(): void {
    this.popoverOpen = false;
    this.fileInput().nativeElement.value = '';
    this.fileInput().nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.uploading = true;
    this.cdr.markForCheck();

    void this.queueSelectedFile(file);
  }

  private async queueSelectedFile(file: File): Promise<void> {
    const fileId = uuidv4();

    try {
      await this.offlineStorage.putBlob(fileId, file);
    } catch (err) {
      this.uploading = false;
      if (this.isQuotaExceededError(err)) {
        this.appToastService.showImageUploadQuotaBlocked();
      } else {
        try {
          await this.offlineStorage.evictBlob(fileId);
        } catch {
          // Cleanup is best effort, so keep the original queue failure visible.
        }
        this.uploadError.emit({ file, error: err });
      }
      this.cdr.markForCheck();
      return;
    }

    try {
      await this.beforePersist()?.();
      await this.offlineStorage.addAction(
        this.http.uploadFile,
        this.checklistId(),
        this.itemId(),
        this.customInputId(),
        fileId,
        file
      );

      this.fileUploaded.emit(this.createLocalMetadata(fileId, file));
      this.offlineStorage.showStorageQuotaWarningIfNeeded();
      this.uploading = false;
      this.cdr.markForCheck();
    } catch (err) {
      try {
        await this.offlineStorage.evictBlob(fileId);
      } catch {
        // Cleanup is best effort, so keep the original queue failure visible.
      }
      this.uploading = false;
      this.uploadError.emit({ file, error: err });
      this.cdr.markForCheck();
    }
  }

  private isQuotaExceededError(error: unknown): boolean {
    return (
      (error instanceof DOMException && error.name === 'QuotaExceededError') ||
      (typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'QuotaExceededError')
    );
  }

  private createLocalMetadata(fileId: string, file: File): FileMetadata {
    const contentType = file.type || 'application/octet-stream';
    return {
      id: fileId,
      checklistId: this.checklistId(),
      fileName: file.name,
      contentType,
      fileSizeBytes: file.size,
      originalFileName: file.name,
      originalContentType: contentType,
      itemId: this.itemId(),
      customInputId: this.customInputId(),
    };
  }
}
